import React from 'react';
import { all, takeEvery, put, call } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { getTenantId, Toast, getIncserveToken } from '../../utils/helper';
import moment from 'moment-timezone';
import {
    GET_POSITION,
    POST_POSITION,
    GET_DOMAIN,
    GET_POSITION_LIST,
    GET_WHITE_LABEL,
    GET_ALL_USERS,
    UPDATE_POSITION_STATUS,
    GENERATE_LINK,
    GET_CANDIDATE_DETAILS_USING_RESUME,
    GET_MANDATORY_SKILL,
    GET_OPTIONAL_SKILL,
    FETCH_SKILLS_JD,
    FETCH_SKILLS,
    GET_GLOBAL_FIELDS,
    GET_POSITION_DETAILS_BY_ID_EMPLOYEE,
} from '../action/types';
import {
    getPositionSuccess,
    getPositionFailure,
    createOrUpdatePositionSuccess,
    createOrUpdatePositionFailure,
    getDomainSuccess,
    getDomainFailure,
    getPositionListSuccess,
    getPositionListFailure,
    getWhiteLabelSuccess,
    getWhiteLabelFailure,
    getAllUsersSuccess,
    getAllUsersFailure,
    updatePositionStatusSuccess,
    updatePositionStatusFailure,
    generateLinkSuccess,
    generateLinkFailure,
    getCandidateDetailsUsingResumeSuccess,
    getCandidateDetailsUsingResumeFailure,
    getMandatorySkillSuccess,
    getMandatorySkillFailure,
    getOptionalSkillSuccess,
    getOptionalSkillFailure,
    fetchSkillsJDSuccess,
    fetchSkillsJDFailure,
    fetchSkillsSuccess,
    fetchSkillsFailure,
    getGlobalFieldsSuccess,
    getGlobalFieldsFailure,
    getPositionDetailsByIdEmployeeSuccess,
    getPositionDetailsByIdEmployeeFailure,
} from '../action';
import INTERVIEW from '../../utils/api/interview';
import SKILL from '../../utils/api/skill';
import USERS from '../../utils/api/users';
import { STATUSES } from '../../component/Questions/utils/constants';

const tenantId = getTenantId();

// Helper function to convert timezone name to {name, offset} format
// This mirrors the behaviour used in candidateSaga so that both generateLink
// and createAndUpdateCandidate send timezone to the backend in the same shape.
const getTimezoneData = (timeZoneName) => {
    if (!timeZoneName) return null;
    try {
        const m = moment.tz(timeZoneName);
        const offset = m.utcOffset(); // offset in minutes
        return {
            name: timeZoneName,
            offset,
        };
    } catch (e) {
        console.error('Error converting timezone in positionSaga:', e);
        return null;
    }
};
function* fetchSkillsRequest(action) {
    try {
        const res = yield SKILL.post(`skills/fetch-skills`, action.payload.data);
        if (res.status === 201 || res.status === 200) {
            yield put(fetchSkillsSuccess());
            if (res.data.result?.error || res.data.result?.message) {
                const message = res.data.result?.error || res.data.result?.message;
                yield toast.error(<Toast msg={message} />, {
                    autoClose: 5000,
                    toastId: message,
                });
            } else {
                yield call(action.payload.callback(res.data.result));
                yield toast.success(<Toast msg={'Skills fetch successfully.'} />, {
                    autoClose: 5000,
                    toastId: 'Skills fetch successfully.',
                });
            }
        }
    } catch (e) {
        yield put(fetchSkillsFailure());
    }
}
function* fetchSkillsJDRequest(action) {
    try {
        const formData = new FormData();
        formData.append('jd', action.payload.jobDescription);
        const res = yield INTERVIEW.post('positions/gptjdSkillset', formData, {
            headers: {
                'content-type': 'multipart/form-data',
                ...(tenantId && { 'x-tenant-id': tenantId }),
            },
        });
        if (res.status === 201 || res.status === 200) {
            yield put(fetchSkillsJDSuccess());
            if (res.data.result?.error && res.data.result?.message) {
                yield toast.error(<Toast msg={res.data.result?.message} />, {
                    autoClose: 5000,
                    toastId: res.data.result?.message,
                });
            } else {
                yield call(action.payload.callback(res.data.result));
                yield toast.success(<Toast msg={'Skills fetch successfully.'} />, {
                    autoClose: 5000,
                    toastId: 'Skills fetch successfully.',
                });
            }
        }
    } catch (e) {
        yield put(fetchSkillsJDFailure());
    }
}
function* getDomainRequest() {
    try {
        const res = yield SKILL.get('domains');
        if (res.status === 200) {
            yield put(getDomainSuccess(res.data.result));
        }
    } catch (e) {
        yield put(getDomainFailure());
    }
}
function* getWhiteLabelRequest() {
    try {
        const res = yield USERS.get('employers/employerLogo');
        if (res.status === 200) {
            yield put(getWhiteLabelSuccess(res.data.result));
        }
    } catch (e) {
        yield put(getWhiteLabelFailure());
    }
}
function* getPositionListRequest() {
    try {
        const res = yield INTERVIEW.get(`positionlists`);
        if (res.status === 200) {
            yield put(getPositionListSuccess(res.data.result));
        }
    } catch (e) {
        yield put(getPositionListFailure());
    }
}
function* getPositionRequest(action) {
    try {
        const isSearch = action.payload?.search ? `&search=${encodeURIComponent(action.payload?.search)}` : '';
        const isPageAndPageSize =
            action.payload?.page && action.payload?.pageSize
                ? `?page=${action.payload.page}&pageSize=${action.payload?.pageSize}`
                : '';
        const isSort = action.payload.filter
            ? action.payload.filter.sort
                ? `&sort=${action.payload.filter.sort}`
                : action.payload.sort
                  ? `&sort=${action.payload.sort}`
                  : ''
            : action.payload.sort
              ? `&sort=${action.payload.sort}`
              : '';
        const isFilter = action.payload.filter
            ? (action.payload.filter.from
                  ? action.payload.filter.to
                      ? `&from=${action.payload.filter.from}T00:00:00&to=${action.payload.filter.to}T23:59:59`
                      : `&from=${action.payload.filter.from}T00:00:00&to=3000-12-31T23:59:59`
                  : action.payload.filter.to
                    ? `&from=2000-01-01T00:00:00&to=${action.payload.filter.to}T23:59:59`
                    : '') +
              (action.payload.filter.status ? `&status=${action.payload.filter.status}` : '') +
              (action.payload.filter.createdBy ? `&createdBy=${action.payload.filter.createdBy}` : '') +
              (action.payload.filter.globalFieldFilters
                  ? Object.entries(action.payload.filter.globalFieldFilters)
                        .filter(([, v]) => v)
                        .map(([k, v]) => `&gf_${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
                        .join('')
                  : '')
            : '';
        const res = yield INTERVIEW.get(`positions${isPageAndPageSize}${isSearch}${isFilter}${isSort}`);
        if (res.status === 201 || res.status === 200) {
            if (action?.payload?.page === 'all') {
                yield put(getPositionFailure());
                yield call(action.payload.callback, res.data.result);
            } else {
                yield put(getPositionSuccess(res.data.result));
            }
        }
    } catch (e) {
        yield put(getPositionFailure());
        yield e?.response?.data?.message &&
            e?.response?.data?.message !== 'Your session has been expired, please login again!' &&
            toast.error(<Toast msg={e?.response?.data?.message} />, {
                autoClose: 5000,
                toastId: e?.response?.data?.message,
            });
    }
}
function* generateLinkRequest(action) {
    try {
        const { timeZone, linkExpiryType, callback, ...rest } = action.payload || {};

        // Base payload always includes existing fields (position, question, startAt, expiredAt, etc.)
        const payload = {
            ...rest,
            ...(linkExpiryType ? { linkExpiryType } : {}),
        };

        // For time-wise expiry, send timezone in the same format as createAndUpdateCandidate:
        // { name: <ianaName>, offset: <minutes> }
        if (timeZone && linkExpiryType === 'time-wise') {
            const timezoneData = getTimezoneData(timeZone);
            if (timezoneData) {
                payload.timezone = timezoneData;
            }
        }

        const res = yield INTERVIEW.post(`incbot/generateLink`, payload);
        if (res.status === 201 || res.status === 200) {
            yield put(generateLinkSuccess(res.data.result));
            if (typeof callback === 'function') {
                yield call(callback);
            }
        }
    } catch (e) {
        yield put(generateLinkFailure());
    }
}
function* postPositionRequest(action) {
    try {
        const formData = new FormData();
        formData.append('title', action.payload.form?.title);
        formData.append('domain', action.payload.form?.domain);
        formData.append(
            'expRange',
            JSON.stringify({
                minYears: action.payload.form?.minExp,
                maxYears: action.payload.form?.maxExp,
            })
        );
        formData.append('skills', JSON.stringify(action?.payload?.skills));
        formData.append('customSkills', JSON.stringify(action?.payload?.customSkills));
        if (action?.payload?.activeSkills)
            formData.append('activeSkills', JSON.stringify(action.payload.activeSkills));
        if (action?.payload?.customFields && action.payload.customFields.length > 0) {
            // Helper function to check if a field is empty
            const isFieldEmpty = (field) => {
                // If field is mandatory, always include it (validation will handle empty mandatory fields)
                if (field.isMandatory === 'yes') {
                    return false;
                }

                // For file type fields, check if file exists
                if (field.fieldType === 'file') {
                    return !field.file && (!field.values || field.values === '');
                }

                // For other field types, check if values is empty
                const fieldValue = field.values;
                return (
                    !fieldValue ||
                    (typeof fieldValue === 'string' && fieldValue.trim() === '') ||
                    (typeof fieldValue === 'object' &&
                        !(fieldValue instanceof File) &&
                        Object.keys(fieldValue).length === 0)
                );
            };

            // Filter out empty non-mandatory fields
            const filteredCustomFields = action.payload.customFields.filter((field) => !isFieldEmpty(field));

            const processedCustomFields = [];

            filteredCustomFields.forEach((field) => {
                if (field.fieldType === 'file' && field.file) {
                    // ✅ Append actual File object separately with consistent key
                    formData.append('customFieldFiles', field.file);

                    // ✅ Only keep metadata in JSON
                    processedCustomFields.push({
                        ...field,
                        file: undefined, // remove raw File to avoid {}
                        fileName: field.file.name,
                    });
                } else {
                    processedCustomFields.push(field);
                }
            });

            // Only append customFields if there are fields to send
            if (processedCustomFields.length > 0) {
                formData.append('customFields', JSON.stringify(processedCustomFields));
            }
        }

        if (action?.payload?.globalFields && Object.keys(action.payload.globalFields).length > 0) {
            formData.append('globalFields', JSON.stringify(action.payload.globalFields));
        }
        // formData.append("noOfPositions", action.payload.form?.noOfPositions);
        if (action.payload.form?.noOfPositions !== '') {
            formData.append('noOfPositions', action.payload.form?.noOfPositions);
        } else {
            formData.append('noOfPositions', 1);
        }
        if (action.payload.form?.positionId) formData.append('positionId', action.payload.form?.positionId);
        if (action.payload.form?.replicateId) formData.append('replicateId', action.payload.form?.replicateId);
        if (action.payload.form?.jobDescription && action?.payload?.fileChange)
            formData.append('jobDescription', action.payload.form?.jobDescription);
        if (action?.payload?.percentageCriteria !== undefined && action?.payload?.percentageCriteria !== null) {
            formData.append('percentageCriteria', action.payload.percentageCriteria / 10);
        }

        let message;
        if (action.payload.form?.positionId) {
            message = 'Position updated successfully.';
        } else {
            message = 'Position created successfully.';
        }
        const incserveToken = getIncserveToken;
        if (incserveToken) formData.append('incserveToken', incserveToken);
        const res = yield INTERVIEW.post(`positions/createAndUpdate`, formData, {
            headers: {
                'content-type': 'multipart/form-data',
            },
        });
        if (res.status === 201) {
            yield put(createOrUpdatePositionSuccess(res.data.result));
            yield call(action.payload.callback(res.data.result));
            yield toast.success(<Toast msg={`${message}`} />, {
                autoClose: 5000,
                toastId: message,
            });
        }
    } catch (e) {
        yield put(createOrUpdatePositionFailure());
        yield e?.response?.data?.message &&
            e?.response?.data?.message !== 'Your session has been expired, please login again!' &&
            toast.error(<Toast msg={e?.response?.data?.message} />, {
                autoClose: 5000,
                toastId: e?.response?.data?.message,
            });
    }
}
function* getCandidateDetailsUsingResumeRequest(action) {
    let formData = new FormData();
    formData.append('cv', action.payload.resume);
    try {
        const res = yield INTERVIEW.post(`interviews/extractDataFromResume`, formData, {
            headers: {
                'content-type': 'multipart/form-data',
                ...(tenantId && { 'x-tenant-id': tenantId }),
            },
        });
        if (res.status === 201 || res.status === 200) {
            yield put(getCandidateDetailsUsingResumeSuccess(res.data));
            if (res.data.email_id || res.data.phone_number) {
                toast.success(<Toast msg={'Candidate Details Fetched Successfully'} />, {
                    autoClose: 5000,
                    toastId: 'Candidate Details Fetched Successfully',
                });
            }
        }
    } catch (e) {
        yield put(getCandidateDetailsUsingResumeFailure());
        e?.response?.data?.message &&
            toast.error(<Toast msg={e?.response?.data?.message} />, {
                autoClose: 5000,
                toastId: e?.response?.data?.message,
            });
    }
}
function* updatePositionStatusRequest(action) {
    try {
        const incserveToken = getIncserveToken;
        const res = yield INTERVIEW.put(
            `positions/changeStatus/${action.payload.positionId}?status=${action.payload.status}${
                incserveToken ? `&incserveToken=${encodeURIComponent(incserveToken)}` : ''
            }`
        );
        if (res.status === 201 || res.status === 200) {
            yield put(updatePositionStatusSuccess(res.data.result));
            toast.success(
                <Toast
                    msg={`${
                        res.data.result.status === STATUSES.CLOSED
                            ? 'Position Closed Successfully'
                            : 'Position Reopen Successfully'
                    }`}
                />,
                {
                    autoClose: 5000,
                    toastId:
                        res.data.result.status === STATUSES.CLOSED
                            ? 'Position Closed Successfully'
                            : 'Position Reopen Successfully',
                }
            );
            yield call(action.payload.callback);
        }
    } catch (e) {
        yield put(updatePositionStatusFailure());
        yield e?.response?.data?.message &&
            e?.response?.data?.message !== 'Your session has been expired, please login again!' &&
            toast.error(<Toast msg={e?.response?.data?.message} />, {
                autoClose: 5000,
                toastId: e?.response?.data?.message,
            });
    }
}
function* getAllUsersRequest() {
    try {
        const res = yield USERS.get(`users/dashboard/dropdown`);
        if (res.status === 200) {
            yield put(getAllUsersSuccess(res.data.result));
        }
    } catch (e) {
        yield put(getAllUsersFailure());
    }
}
function* getMandatorySkillRequest() {
    try {
        const res = yield SKILL.get(`skills?type=mandatory`);
        if (res.status === 200) {
            yield put(getMandatorySkillSuccess(res.data.result));
        }
    } catch (e) {
        yield put(getMandatorySkillFailure());
        yield e?.response?.data?.message &&
            e?.response?.data?.message !== 'Your session has been expired, please login again!' &&
            toast.error(<Toast msg={e?.response?.data?.message} />, {
                toastId: e?.response?.data?.message,
            });
    }
}

function* getOptionalSkillRequest() {
    try {
        const res = yield SKILL.get(`skills?type=optional`);
        if (res.status === 200) {
            yield put(getOptionalSkillSuccess(res.data.result));
        }
    } catch (e) {
        yield put(getOptionalSkillFailure());
        yield e?.response?.data?.message &&
            e?.response?.data?.message !== 'Your session has been expired, please login again!' &&
            toast.error(<Toast msg={e?.response?.data?.message} />, {
                toastId: e?.response?.data?.message,
            });
    }
}

function* getPositionDetailsByIdEmployeeRequest(action) {
    try {
        const { positionId, callback, failure } = action.payload;
        const res = yield INTERVIEW.get(`positions/${positionId}`);
        if (res.status === 200 || res.status === 201) {
            yield put(getPositionDetailsByIdEmployeeSuccess(res.data.result));
            if (callback) {
                yield call(callback, res.data.result);
            }
        } else {
            yield put(getPositionDetailsByIdEmployeeFailure());
            if (failure) {
                yield call(failure);
            }
        }
    } catch (e) {
        yield put(getPositionDetailsByIdEmployeeFailure());
        if (action.payload?.failure) {
            yield call(action.payload.failure);
        }
        yield e?.response?.data?.message &&
            e?.response?.data?.message !== 'Your session has been expired, please login again!' &&
            toast.error(<Toast msg={e?.response?.data?.message} />, {
                autoClose: 5000,
                toastId: e?.response?.data?.message,
            });
    }
}

function* getGlobalFieldsRequest(action) {
    try {
        const params = {
            page: action?.payload?.page || 1,
            pageSize: action?.payload?.pageSize || 100,
            ...(action?.payload?.fieldMode && { fieldMode: action.payload.fieldMode }),
            ...(action?.payload?.productFilterLabel && { productFilterLabel: action.payload.productFilterLabel }),
        };
        const res = yield INTERVIEW.get('interviews/CustomFields/list', { params });
        if (res.status === 201 || res.status === 200) {
            yield put(getGlobalFieldsSuccess(res.data.result));
        }
    } catch (e) {
        yield put(getGlobalFieldsFailure());
    }
}
export function* watchGetPositionAPI() {
    yield takeEvery(GET_POSITION, getPositionRequest);
}
export function* watchPostPositionAPI() {
    yield takeEvery(POST_POSITION, postPositionRequest);
}
export function* watchGenerateLinkAPI() {
    yield takeEvery(GENERATE_LINK, generateLinkRequest);
}
export function* watchGetDomainAPI() {
    yield takeEvery(GET_DOMAIN, getDomainRequest);
}
export function* watchGetCandidateDetailsUsingResumeRequestAPI() {
    yield takeEvery(GET_CANDIDATE_DETAILS_USING_RESUME, getCandidateDetailsUsingResumeRequest);
}
export function* watchGetWhiteLabelAPI() {
    yield takeEvery(GET_WHITE_LABEL, getWhiteLabelRequest);
}
export function* watchGetPositionListAPI() {
    yield takeEvery(GET_POSITION_LIST, getPositionListRequest);
}
export function* watchGetAllUsersAPI() {
    yield takeEvery(GET_ALL_USERS, getAllUsersRequest);
}
export function* watchUpdatePositionStatusAPI() {
    yield takeEvery(UPDATE_POSITION_STATUS, updatePositionStatusRequest);
}
export function* watchGetMandatorySkillAPI() {
    yield takeEvery(GET_MANDATORY_SKILL, getMandatorySkillRequest);
}
export function* watchGetOptionalSkillAPI() {
    yield takeEvery(GET_OPTIONAL_SKILL, getOptionalSkillRequest);
}
export function* watchFetchSkillsAPI() {
    yield takeEvery(FETCH_SKILLS, fetchSkillsRequest);
}
export function* watchFetchSkillsJDAPI() {
    yield takeEvery(FETCH_SKILLS_JD, fetchSkillsJDRequest);
}
export function* watchGetGlobalFieldsAPI() {
    yield takeEvery(GET_GLOBAL_FIELDS, getGlobalFieldsRequest);
}

export function* watchGetPositionDetailsByIdEmployeeAPI() {
    yield takeEvery(GET_POSITION_DETAILS_BY_ID_EMPLOYEE, getPositionDetailsByIdEmployeeRequest);
}

export default function* rootSaga() {
    yield all([
        watchGenerateLinkAPI(),
        watchGetPositionAPI(),
        watchPostPositionAPI(),
        watchGetDomainAPI(),
        watchGetWhiteLabelAPI(),
        watchGetPositionListAPI(),
        watchGetAllUsersAPI(),
        watchUpdatePositionStatusAPI(),
        watchGetCandidateDetailsUsingResumeRequestAPI(),
        watchGetMandatorySkillAPI(),
        watchGetOptionalSkillAPI(),
        watchFetchSkillsAPI(),
        watchFetchSkillsJDAPI(),
        watchGetGlobalFieldsAPI(),
        watchGetPositionDetailsByIdEmployeeAPI(),
    ]);
}
